import { useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useRecoilValue } from "recoil";
import { useStatsigClient } from "@statsig/react-bindings";

import { SUGGESTED_FOLLOWERS } from "MVVM/GraphQL/suggestedFollowers";
import { ISuggestedUser, ESuggestedFollowReason } from "../@types";
import { useUser } from "MVVM/Hooks/useUser";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";

const LEGACY_REASON_MAPPING: Record<string, string> = {
  PLAYED_TOGETHER: "Played Together",
  FOLLOWED_BY_FOLLOWING: "Followed by Following",
  RISING_STAR: "Rising Star",
  UNSPECIFIED: "Unspecified",
  POPULAR_USER: "Popular User",
};

const mapReasonToDisplay = (reason: string): string => {
  if (LEGACY_REASON_MAPPING[reason]) {
    return LEGACY_REASON_MAPPING[reason];
  }

  if (ESuggestedFollowReason[reason as keyof typeof ESuggestedFollowReason]) {
    return ESuggestedFollowReason[
      reason as keyof typeof ESuggestedFollowReason
    ];
  }

  const enumValues = Object.values(ESuggestedFollowReason);
  if (enumValues.includes(reason as any)) {
    return reason;
  }

  return ESuggestedFollowReason.UNSPECIFIED;
};

const SuggestedFeedViewModel = () => {
  const { allstarUser } = useUser();
  const loggedIn = allstarUser?.loggedIn;
  const { getExperiment } = useStatsigClient();
  const suggestedFollowing: { ids: string[] } =
    useRecoilValue(SuggestedFollowing);

  let useGraphQuery = false;
  if (loggedIn) {
    useGraphQuery = getExperiment("graphbasedsuggestedfollowers").get(
      "use_graph_query",
      false,
    );
  }

  const { data, error, loading } = useQuery(
    SUGGESTED_FOLLOWERS(useGraphQuery),
    {
      fetchPolicy: "network-only",
      variables: { count: 12 },
    },
  );

  const filteredSuggestedFollowers = useCallback(() => {
    const suggestedFollowers = data?.me?.suggestions;
    if (!suggestedFollowers) return [];

    const filtered = suggestedFollowers.filter(
      ({ user: { _id } }: { user: { _id: string } }) =>
        !suggestedFollowing.ids.includes(_id),
    );

    const mappedFiltered = filtered.map((suggestion: any) => ({
      ...suggestion,
      reason: mapReasonToDisplay(suggestion.reason),
    }));

    return mappedFiltered.length > 10
      ? mappedFiltered
      : suggestedFollowers.slice(0, 12).map((suggestion: any) => ({
          ...suggestion,
          reason: mapReasonToDisplay(suggestion.reason),
        }));
  }, [data?.me?.suggestions]);

  const suggestedFollowers = useGraphQuery
    ? filteredSuggestedFollowers()
    : data?.suggestedCreators.map((suggested: ISuggestedUser) => {
        const mappedReason = mapReasonToDisplay(suggested.reason);

        return {
          user: suggested.user,
          reason: mappedReason,
        };
      });

  return {
    suggestedFollowers,
    error,
    loading,
  };
};

export { SuggestedFeedViewModel };
